// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { catchError, switchMap, tap } from 'rxjs/operators';

// --- 3rdp Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { DemographicValueModel, ApplicationError, ToastModel } from '../../../models';
import { UserDemographicsBaseService } from './user-demographics-base.service';
import { environment } from '../../../../environments/environment';
import { of, Observable } from 'rxjs';
import { AppService } from '../app.service';
import { DemographicsBaseService } from '../system/demographics-base.service';
import { AppToastService } from '../app-toast.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class UserDemographicsService extends UserDemographicsBaseService {
  constructor(
    protected appService: AppService,
    protected demographicService: DemographicsBaseService,
    private http: HttpClient,
    private translate: TranslateService,
    private toastService: AppToastService
  ) {
    super(appService, demographicService);
  }

  loadUserDemographicValues(): void {
    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.userDemographicsApi;

    this.http
      .get<DemographicValueModel[]>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      )
      .subscribe(result => {
        this._userDemographicValues$.next(result);
        this._processUserDemographics(result);
      });
  }

  updateUserDemographics(
    userDemographicValues: DemographicValueModel[]
  ): Observable<boolean> {
    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.userDemographicsApi;

    return this.http
      .put<DemographicValueModel[]>(url, userDemographicValues)
      .pipe(
        retryBackoff(environment.retryConfig),
        tap(result => {
          this._userDemographicValues$.next(result);
        }),
        switchMap(() => of(true)),
        catchError(this._modelError.bind(this))
      );
  }

  updateUserDemographicsFromProfile(userDemographicValues: DemographicValueModel[]): void {
    if (!userDemographicValues || userDemographicValues.length === 0) {
      return;
    }

    const url =
      environment.apiConfig.baseUrl + environment.apiConfig.userDemographicsApi;

    this.http
      .put<DemographicValueModel[]>(url, userDemographicValues)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      ).subscribe(result => {
        this.toastService.show({
          header: this.translate.instant('updateSuccessHeader'),
          body: this.translate.instant('updateUserDemographicsSuccessBody'),
          class: 'bg-success text-light'
        } as ToastModel);
        this._userDemographicValues$.next(result);
        this._processUserDemographics(result);
        this._updateUserDemographicsResult$.next(true);
      });
  }

  private _modelError(err, caught) {
    this._handleError(err);
    this._updateUserDemographicsResult$.next(false);
    return of(null as DemographicValueModel[]);
  }

  private _updateError(err, caught) {
    this._handleError(err);
    return of(false);
  }

  private _handleError(err) {
    this.appService.setLoading(false);

    let error: ApplicationError;
    if (err && err instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      let detailedMessage = `${err.message}`;
      if (err.statusText) {
        detailedMessage += `<br/>${err.statusText}`;
      }
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        errorNumber: err.status,
        detailedMessage
      } as ApplicationError;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      error = {
        errorMessage: this.translate.instant('errorMessageGenericBackend'),
        originalError: err
      } as ApplicationError;
    }
    this.appService.setLastError(error);
  }
}
